// frontend/src/pages/Portfolio.js
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService, getFullImageUrl } from '../services/api';
import SEOHead from '../components/SEOHead';
import { 
  Search, 
  Users, 
  Briefcase, 
  Award, 
  Github, 
  Linkedin, 
  Mail, 
  ExternalLink,
  Filter,
  X,
  Star,
  MapPin,
  Calendar,
  Phone
} from 'lucide-react';

const Portfolio = () => {
  const [members, setMembers] = useState([]);
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterPosition, setFilterPosition] = useState('');
  const [filterSkill, setFilterSkill] = useState('');
  const [activeView, setActiveView] = useState('grid');
  const [selectedMember, setSelectedMember] = useState(null);

  useEffect(() => {
    fetchMembers();
  }, []);

  useEffect(() => {
    filterMembers();
  }, [members, searchQuery, filterPosition, filterSkill]);

  const fetchMembers = async () => {
    try {
      const response = await apiService.getTeamMembers();
      setMembers(response.data.results || response.data);
    } catch (error) {
      console.error('Error fetching team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterMembers = () => {
    let filtered = members;

    // فیلتر بر اساس جستجو
    if (searchQuery) {
      filtered = filtered.filter(member =>
        member.full_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.bio?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        member.skills?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // فیلتر بر اساس موقعیت شغلی
    if (filterPosition) {
      filtered = filtered.filter(member =>
        member.position === filterPosition
      );
    }

    // فیلتر بر اساس مهارت
    if (filterSkill) {
      filtered = filtered.filter(member =>
        member.skills_list?.some(skill => 
          skill.toLowerCase().includes(filterSkill.toLowerCase())
        )
      );
    }

    setFilteredMembers(filtered);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    filterMembers();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setFilterPosition('');
    setFilterSkill('');
  };

  const positionOptions = [
    { value: '', label: 'همه موقعیت‌ها' },
    { value: 'manager', label: 'مدیریت' },
    { value: 'developer', label: 'توسعه‌دهنده' },
    { value: 'designer', label: 'طراح' },
    { value: 'marketing', label: 'بازاریابی' },
    { value: 'support', label: 'پشتیبانی' },
    { value: 'other', label: 'سایر' },
  ];

  // استخراج تمام مهارت‌های منحصر به فرد
  const allSkills = [...new Set(
    members.flatMap(member => member.skills_list || [])
  )].sort();

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchQuery) count++;
    if (filterPosition) count++;
    if (filterSkill) count++;
    return count;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title="تیم متخصصان - BusinessPro"
        description="گروه صنعتی تکنو پلیمر اسپادانا طراحی و ساخت انواع قالب های لاستیک و تولید انواع قطعات لاستیکی از جمله اورینگ، پکینگ، قطعات صنعتی و خودرویی"
        keywords="طراحی و ساخت انواع قالب، تولید قطعات پلاستیکی ،قالب سازی، لاستیک سازی، اورینگ، سیلیکون، پکینگ، دیافراگم، واشر"
        image={getFullImageUrl('/static/images/og-team.jpg')}
      />
      
      <div className="min-h-screen bg-black py-8">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-12 fade-in">
            <h1 className="text-4xl font-bold text-white mb-4">👥 تیم متخصصان تکنو پلیمر اسپادانا</h1>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              با تیم متخصص و با تجربه ما آشنا شوید. هر یک از اعضای تیم ما دارای مهارت‌های منحصر به فرد و تجربه ارزشمند در حوزه صنعت هستند.
            </p>
          </div>

          {/* Filters and Search */}
          <div className="bg-gray-900 rounded-2xl p-6 mb-8 border border-gray-800">
            <div className="flex flex-col lg:flex-row gap-6 items-center justify-between">
              {/* Search */}
              <form onSubmit={handleSearch} className="flex-1 w-full lg:w-auto">
                <div className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="جستجو در اعضای تیم (نام، مهارت، موقعیت)..."
                    className="w-full lg:w-96 pl-12 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-xl text-white placeholder-gray-400 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  />
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                </div>
              </form>

              <div className="flex items-center space-x-4 space-x-reverse">
                {/* View Toggle */}
                <div className="flex items-center space-x-2 space-x-reverse bg-gray-800 rounded-xl p-1">
                  <button
                    onClick={() => setActiveView('grid')}
                    className={`p-2 rounded-lg transition-colors ${
                      activeView === 'grid' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                    }`}
                  >
                    <div className="w-4 h-4 grid grid-cols-2 gap-0.5">
                      <div className="bg-current rounded-sm"></div>
                      <div className="bg-current rounded-sm"></div>
                      <div className="bg-current rounded-sm"></div>
                      <div className="bg-current rounded-sm"></div>
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveView('list')}
                    className={`p-2 rounded-lg transition-colors ${
                      activeView === 'list' ? 'bg-gray-700 text-yellow-400' : 'text-gray-400'
                    }`}
                  >
                    <div className="w-4 h-4 flex flex-col space-y-0.5">
                      <div className="bg-current rounded-sm h-1"></div>
                      <div className="bg-current rounded-sm h-1"></div>
                      <div className="bg-current rounded-sm h-1"></div>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Advanced Filters */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {/* Position Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  موقعیت شغلی:
                </label>
                <select
                  value={filterPosition}
                  onChange={(e) => setFilterPosition(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                >
                  {positionOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Skill Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  مهارت:
                </label>
                <select
                  value={filterSkill}
                  onChange={(e) => setFilterSkill(e.target.value)}
                  className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                >
                  <option value="">همه مهارت‌ها</option>
                  {allSkills.map(skill => (
                    <option key={skill} value={skill}>
                      {skill}
                    </option>
                  ))}
                </select>
              </div>

              {/* Active Filters */}
              <div className="flex items-end">
                {getActiveFiltersCount() > 0 && (
                  <div className="flex items-center space-x-3 space-x-reverse">
                    <span className="text-gray-300 text-sm">
                      {filteredMembers.length} عضو یافت شد
                    </span>
                    <button
                      onClick={clearFilters}
                      className="flex items-center space-x-1 space-x-reverse text-gray-400 hover:text-white transition-colors text-sm"
                    >
                      <X size={16} />
                      <span>پاک کردن فیلترها</span>
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Active Filters Tags */}
            {(searchQuery || filterPosition || filterSkill) && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-gray-800">
                {searchQuery && (
                  <span className="bg-yellow-400 text-gray-900 px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 space-x-reverse">
                    <span>جستجو: {searchQuery}</span>
                    <button onClick={() => setSearchQuery('')}>
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filterPosition && (
                  <span className="bg-blue-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 space-x-reverse">
                    <span>موقعیت: {positionOptions.find(p => p.value === filterPosition)?.label}</span>
                    <button onClick={() => setFilterPosition('')}>
                      <X size={14} />
                    </button>
                  </span>
                )}
                {filterSkill && (
                  <span className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1 space-x-reverse">
                    <span>مهارت: {filterSkill}</span>
                    <button onClick={() => setFilterSkill('')}>
                      <X size={14} />
                    </button>
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Members Grid/List */}
          {filteredMembers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="text-gray-400 h-16 w-16 mx-auto mb-4" />
              <h3 className="text-xl font-medium text-white mb-2">
                {members.length === 0 ? 'هنوز عضوی اضافه نشده' : 'عضو تیمی یافت نشد'}
              </h3>
              <p className="text-gray-400 mb-6">
                {members.length === 0 
                  ? 'به زودی اطلاعات تیم اضافه خواهد شد.' 
                  : 'لطفاً فیلترهای جستجو را تغییر دهید.'
                }
              </p>
              {members.length > 0 && (
                <button
                  onClick={clearFilters}
                  className="bg-yellow-400 text-gray-900 px-6 py-2 rounded-lg hover:bg-yellow-300 transition-colors"
                >
                  نمایش همه اعضا
                </button>
              )}
            </div>
          ) : (
            <>
              {/* View Toggle Header */}
              <div className="flex items-center justify-between mb-6">
                <div className="text-gray-300">
                  نمایش {filteredMembers.length} عضو از {members.length} عضو
                </div>
                <div className="text-sm text-gray-400">
                  {activeView === 'grid' ? 'نمایش گریدی' : 'نمایش لیستی'}
                </div>
              </div>

              {/* Members Display */}
              <div className={
                activeView === 'grid' 
                  ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                  : 'space-y-6'
              }>
                {filteredMembers.map((member) => (
                  <TeamMemberCard 
                    key={member.id} 
                    member={member} 
                    viewMode={activeView}
                    onMemberClick={setSelectedMember}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Member Modal */}
        {selectedMember && (
          <MemberModal 
            member={selectedMember} 
            onClose={() => setSelectedMember(null)} 
          />
        )}
      </div>
    </>
  );
};

const TeamMemberCard = ({ member, viewMode, onMemberClick }) => {
  if (viewMode === 'list') {
    return (
      <div 
        className="bg-gray-900 rounded-2xl p-6 border border-gray-800 hover:border-yellow-400 transition-all cursor-pointer group"
        onClick={() => onMemberClick(member)}
      >
        <div className="flex items-start space-x-4 space-x-reverse">
          {/* Profile Image */}
          <div className="flex-shrink-0">
            <div className="relative">
              <img
                src={getFullImageUrl(member.profile_image)}
                alt={member.full_name}
                className="w-20 h-20 rounded-2xl object-cover border-2 border-gray-700 group-hover:border-yellow-400 transition-colors"
              />
              {member.is_featured && (
                <div className="absolute -top-2 -right-2 bg-yellow-400 text-gray-900 p-1 rounded-full">
                  <Star size={12} fill="currentColor" />
                </div>
              )}
            </div>
          </div>

          {/* Member Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between mb-2">
              <div>
                <h3 className="text-xl font-bold text-white group-hover:text-yellow-400 transition-colors mb-1">
                  {member.full_name}
                </h3>
                <p className="text-yellow-400 font-medium text-sm capitalize">
                  {member.position}
                </p>
              </div>
              
              <div className="flex items-center space-x-2 space-x-reverse text-gray-400">
                <Briefcase size={16} />
                <span className="text-sm">{member.projects_count || 0}</span>
              </div>
            </div>

            <p className="text-gray-300 text-sm mb-3 line-clamp-2">
              {member.bio?.replace(/<[^>]*>/g, '').substring(0, 150)}...
            </p>

            {/* Skills */}
            {member.skills_list && member.skills_list.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-3">
                {member.skills_list.slice(0, 4).map((skill, index) => (
                  <span
                    key={index}
                    className="bg-gray-800 text-gray-300 px-2 py-1 rounded text-xs border border-gray-700"
                  >
                    {skill}
                  </span>
                ))}
                {member.skills_list.length > 4 && (
                  <span className="bg-gray-800 text-gray-400 px-2 py-1 rounded text-xs border border-gray-700">
                    +{member.skills_list.length - 4}
                  </span>
                )}
              </div>
            )}

            {/* Social Links */}
            <div className="flex items-center space-x-3 space-x-reverse">
              {(member.linkedin || member.github) && (
                <div className="flex space-x-2 space-x-reverse">
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-400 hover:text-blue-400 transition-colors"
                    >
                      <Linkedin size={16} />
                    </a>
                  )}
                  {member.github && (
                    <a
                      href={member.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-gray-400 hover:text-gray-300 transition-colors"
                    >
                      <Github size={16} />
                    </a>
                  )}
                </div>
              )}
              
              <button className="text-yellow-400 hover:text-yellow-300 text-sm font-medium flex items-center space-x-1 space-x-reverse">
                <span>مشاهده پروفایل</span>
                <ExternalLink size={14} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Grid View
  return (
    <div 
      className="bg-gray-900 rounded-2xl overflow-hidden border border-gray-800 hover:border-yellow-400 transition-all group cursor-pointer"
      onClick={() => onMemberClick(member)}
    >
      {/* Profile Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={getFullImageUrl(member.profile_image)}
          alt={member.full_name}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent opacity-60"></div>
        
        {/* Featured Badge */}
        {member.is_featured && (
          <div className="absolute top-3 left-3 bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold flex items-center space-x-1 space-x-reverse">
            <Star size={12} fill="currentColor" />
            <span>ویژه</span>
          </div>
        )}

        {/* Social Links */}
        <div className="absolute bottom-3 left-3 flex space-x-2 space-x-reverse">
          {member.linkedin && (
            <a
              href={member.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors opacity-0 group-hover:opacity-100"
            >
              <Linkedin size={16} />
            </a>
          )}
          {member.github && (
            <a
              href={member.github}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-700 text-white p-2 rounded-lg hover:bg-gray-600 transition-colors opacity-0 group-hover:opacity-100"
            >
              <Github size={16} />
            </a>
          )}
        </div>
      </div>

      {/* Member Info */}
      <div className="p-5">
        <h3 className="text-xl font-bold text-white mb-2 group-hover:text-yellow-400 transition-colors line-clamp-1">
          {member.full_name}
        </h3>
        
        <p className="text-yellow-400 font-medium mb-3 capitalize text-sm">
          {member.position}
        </p>

        <p className="text-gray-300 text-sm mb-4 line-clamp-3 leading-relaxed">
          {member.bio?.replace(/<[^>]*>/g, '').substring(0, 120)}...
        </p>

        {/* Skills */}
        {member.skills_list && member.skills_list.length > 0 && (
          <div className="mb-4">
            <div className="flex flex-wrap gap-1">
              {member.skills_list.slice(0, 3).map((skill, index) => (
                <span
                  key={index}
                  className="bg-gray-800 text-gray-300 px-2 py-1 rounded text-xs border border-gray-700"
                >
                  {skill}
                </span>
              ))}
              {member.skills_list.length > 3 && (
                <span className="bg-gray-800 text-gray-400 px-2 py-1 rounded text-xs border border-gray-700">
                  +{member.skills_list.length - 3}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Stats */}
        <div className="flex items-center justify-between text-gray-400 text-sm border-t border-gray-800 pt-4">
          <div className="flex items-center space-x-4 space-x-reverse">
            <div className="flex items-center space-x-1 space-x-reverse">
              <Briefcase size={14} />
              <span>{member.projects_count || 0} پروژه</span>
            </div>
            {member.experience && (
              <div className="flex items-center space-x-1 space-x-reverse">
                <Award size={14} />
                <span>{member.experience}+ سال</span>
              </div>
            )}
          </div>
          
          <div className="text-yellow-400 hover:text-yellow-300 font-medium flex items-center space-x-1 space-x-reverse">
            <span>مشاهده</span>
            <ExternalLink size={14} />
          </div>
        </div>
      </div>
    </div>
  );
};

// مودال نمایش جزئیات عضو تیم
const MemberModal = ({ member, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4">
      <div className="bg-gray-900 rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto border border-gray-800">
        {/* Header */}
        <div className="relative">
          {/* Cover Image (if exists) */}
          {member.cover_image && (
            <div className="h-48 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-t-2xl">
              <img
                src={getFullImageUrl(member.cover_image)}
                alt={`Cover ${member.full_name}`}
                className="w-full h-full object-cover rounded-t-2xl opacity-20"
              />
            </div>
          )}
          
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 left-4 bg-black bg-opacity-50 text-white p-2 rounded-full hover:bg-opacity-70 transition-all"
          >
            <X size={20} />
          </button>
        </div>

        <div className="p-6">
          <div className="flex flex-col md:flex-row items-start md:items-center space-y-6 md:space-y-0 md:space-x-6 md:space-x-reverse">
            {/* Profile Image */}
            <div className="relative md:-mt-1">
              <img
                src={getFullImageUrl(member.profile_image)}
                alt={member.full_name}
                className="w-32 h-32 md:w-40 md:h-40 rounded-2xl border-4 border-gray-900 object-cover"
              />
            </div>

            {/* Basic Info */}
            <div className="flex-1">
              <h2 className="text-3xl font-bold text-white m2-2">
                {member.full_name}
              </h2>
              <p className="text-xl text-yellow-400 font-medium mb-4 capitalize">
                {member.position}
              </p>
              
              {/* Contact Info */}
              <div className="flex flex-wrap gap-4 text-gray-300 mb-4">
                {member.email && (
                  <a href={`mailto:${member.email}`} className="flex items-center space-x-2 space-x-reverse hover:text-white transition-colors">
                    <Mail size={16} />
                    <span>ایمیل</span>
                  </a>
                )}
                {member.phone && (
                  <a href={`tel:${member.phone}`} className="flex items-center space-x-2 space-x-reverse hover:text-white transition-colors">
                    <Phone size={16} />
                    <span>تماس</span>
                  </a>
                )}
              </div>

              {/* Social Links */}
              <div className="flex space-x-3 space-x-reverse">
                {member.linkedin && (
                  <a href={member.linkedin} target="_blank" rel="noopener noreferrer" className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 transition-colors">
                    <Linkedin size={20} />
                  </a>
                )}
                {member.github && (
                  <a href={member.github} target="_blank" rel="noopener noreferrer" className="bg-gray-700 text-white p-2 rounded-lg hover:bg-gray-600 transition-colors">
                    <Github size={20} />
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Bio */}
          <div className="mt-8">
            <h3 className="text-xl font-bold text-white mb-2">درباره من</h3>
            <div 
              className="prose prose-invert max-w-none text-gray-300 leading-8"
              dangerouslySetInnerHTML={{ __html: member.bio }}
            />
          </div>

          {/* Skills */}
          {member.skills_list && member.skills_list.length > 0 && (
            <div className="mt-8">
              <h3 className="text-xl font-bold text-white mb-4">مهارت‌ها</h3>
              <div className="flex flex-wrap gap-2">
                {member.skills_list.map((skill, index) => (
                  <span
                    key={index}
                    className="bg-gray-800 text-gray-300 px-3 py-2 rounded-lg text-sm border border-gray-700"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 mt-8 pt-8 border-t border-gray-800">
            <Link
              to={`/portfolio/members/${member.id}`}
              className="flex-1 bg-yellow-400 text-gray-900 py-3 px-6 rounded-xl font-bold hover:bg-yellow-300 transition-all text-center"
            >
              مشاهده پروفایل کامل
            </Link>
            <button
              onClick={onClose}
              className="flex-1 border-2 border-gray-700 text-gray-300 py-3 px-6 rounded-xl font-bold hover:border-gray-600 hover:text-white transition-all"
            >
              بستن
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;